<template>
  <d2-container>
    <top-line :message="pageTitle" :iconclass="'fa fa-users'"></top-line>
    <div class="container" style="padding: 20px;">
      <!-- 第一部分：统计信息 -->
      <el-card class="box-card" style="margin-bottom: 20px;">
        <div slot="header" class="clearfix">
          <span style="font-size: 18px; font-weight: bold;">设备统计概览</span>
        </div>
        
        <!-- 提供商统计 -->
        <div v-if="providerStats.length > 0" style="margin-bottom: 30px; width: 45%; float: left;">
          <h3 style="margin-bottom: 15px; color: #409EFF;">提供商统计</h3>
          <div class="simple-stats-row" v-for="(provider, index) in providerStats" :key="'provider-' + index" :style="{ marginTop: index > 0 ? '15px' : '0' }">
            <div class="simple-stat-item">
              <span class="simple-label">{{ provider.provider }}</span>
              <span class="simple-value">总数: {{ provider.total }}</span>
              <span class="simple-value" :style="{ color: 'orange' }">&nbsp;占用: {{ provider.used }}</span>
              <span class="simple-value" :style="{ color: 'green' }">&nbsp;空闲: {{ provider.idle }}</span>
            </div>
          </div>
        </div>

        <!-- 设备类型统计 -->
        <div v-if="deviceTypeStats.length > 0" style="margin-bottom: 30px; width: 45%; float: right;">
          <h3 style="margin-bottom: 15px; color: #409EFF; ">设备类型统计</h3>
          <div class="simple-stats-row" v-for="(device, index) in deviceTypeStats" :key="'device-' + index" :style="{ marginTop: index > 0 ? '15px' : '0' }">
            <div class="simple-stat-item">
              <span class="simple-label">{{ device.ext_06 }}</span>
              <span class="simple-value">总数: {{ device.total }}</span>
              <span class="simple-value" :style="{ color: 'green' }">&nbsp;空闲: {{ device.idle }}</span>
              <span class="simple-value" :style="{ color: 'orange' }">&nbsp;租赁: {{ device.rented }}</span>
              <span class="simple-value" :style="{ color: 'blue' }">&nbsp;测试: {{ device.testing }}</span>
            </div>
          </div>
        </div>
      </el-card>

      <!-- 第二部分：订单列表 -->
      <el-card class="box-card" style="margin-bottom: 20px;">
        <div slot="header" class="clearfix">
          <span style="font-size: 18px; font-weight: bold;">当前租赁订单</span>
        </div>
        <el-table 
          :data="orders" 
          border 
          style="width: 100%"
          :default-sort="{ prop: 'device_count', order: 'descending' }" 
        >
          <el-table-column 
            prop="customer_name" 
            label="客户名称" 
            sortable 
          />
          <el-table-column 
            prop="spec_type" 
            label="资源类型" 
            sortable 
          />
          <el-table-column 
            prop="device_count" 
            label="设备数量" 
            sortable 
          />
          <el-table-column 
            label="订单类型" 
            :sortable="true" 
            :sort-by="row => row.order_type"
          >
            <template #default="scope">
              <el-tag 
                :type="getOrderTypeTagType(scope.row.order_type)"
                size="small"
              >
                {{ getOrderTypeText(scope.row.order_type) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column 
            prop="starting_time" 
            label="开始时间" 
            :formatter="formatDate" 
          />
          <el-table-column 
            prop="end_time" 
            label="结束时间" 
            :formatter="formatDate" 
          />
        </el-table>
      </el-card>

      <!-- 第三部分：设备节点列表（带筛选标签） -->
      <el-card class="box-card">
        <div slot="header" class="clearfix">
          <span style="font-size: 18px; font-weight: bold;">设备节点详情</span>
          <el-tag
            :type="selectedComputeTypes.length === 0 ? 'primary' : 'info'"
            size="small"
            style="margin-left: 10px; cursor: pointer;"
            @click="selectedComputeTypes = []; pagination.page = 1; fetchDevices()"
          >
            全部
          </el-tag>
          <el-tag
            v-for="type in computeTypes"
            :key="type.name"
            :type="selectedComputeTypes.includes(type.name) ? 'primary' : 'info'"
            size="small"
            style="margin-left: 10px; cursor: pointer;"
            @click="handleTypeToggle(type.name)"
          >
            {{ type.name }}
          </el-tag>
        </div>
        <el-table
          :data="devices"
          border
          style="width: 100%"
          :default-sort="{ prop: 'host_ip', order: 'ascending' }"
        >
          <el-table-column
            prop="host_ip"
            label="节点IP"
            sortable
            :sort-method="ipSortMethod"
          />
          <el-table-column
            prop="hostname"
            label="节点名称"
            sortable
          />
          <el-table-column prop="spec_type" label="GPU类型" />
          
          <el-table-column label="是否占用">
            <template slot-scope="scope">
              <el-tag :type="getStatusTagType(scope.row.active_state)">
                <span :style="{ color: getStatusColor(scope.row.active_state), marginRight: '5px' }">●</span>
                {{ getStatusText(scope.row.active_state) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="customer_name" label="客户名称" width="160" />
          <el-table-column label="开始占用时间">
            <template slot-scope="scope">
              {{ scope.row.start_time && scope.row.active_state === 1 ? formatDate(scope.row, null, scope.row.start_time) : '' }}
            </template>
          </el-table-column>
          <el-table-column label="预期释放时间">
            <template slot-scope="scope">
              {{ scope.row.end_time && scope.row.active_state === 1 ? scope.row.end_time : '' }}
            </template>
          </el-table-column>
        </el-table>
        <div style="margin-top: 20px; display: flex; justify-content: flex-end;">
          <el-pagination
            @size-change="handlePageSizeChange"
            @current-change="handlePageChange"
            :current-page="pagination.page"
            :page-sizes="pagination.pageSizeList"
            :page-size="pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
          />
        </div>
      </el-card>
    </div>
  </d2-container>
</template>

<script>
import topLine from "@/components/save-common/topLine.vue";

export default {
  name: 'DeviceDashboard',
  components: {
    topLine,
  },
  data() {
    return {
      pageTitle: "资源使用情况监控",
      providerStats: [],
      deviceTypeStats: [],
      computeTypes: [],
      selectedComputeTypes: [],
      orders: [],
      devices: [],
      pagination: {
        page: 1,
        pageSize: 10,
        pageSizeList: [10, 20, 50, 100],
        total: 0
      }
    };
  },

  created() {
    this.fetchProviderStats();
    this.fetchDeviceTypeStats();
    this.fetchOrders();
    this.fetchComputeTypes();
    this.fetchDevices();
  },
  methods: {
    getOrderTypeText(type) {
      const map = { 
        0: '开口合同', 
        1: '闭口合同', 
        2: '售前测试' 
      };
      return map[type] || '未知';
    },
    getOrderTypeTagType(type) {
      const tagMap = {
        0: '',         // 开口 - 蓝色
        1: 'warning',  // 闭口 - 黄色
        2: 'info'      // 测试 - 灰色
      };
      return tagMap[type] || '';
    },
    formatDate(row, column, cellValue) {
      const value = cellValue || row.start_time || row.end_time;
      if (!value) return '';
      return value.substring(0, 10);
    },
    ipToNumber(ip) {
      return ip.split('.').map(Number).reduce((acc, octet) => acc * 256 + octet, 0);
    },
    ipSortMethod(a, b) {
      return this.ipToNumber(a.host_ip) - this.ipToNumber(b.host_ip);
    },
    async fetchProviderStats() {
      try {
        const res = await this.$axios.get('/api-30216/api/statistics/provider');
        if (res.code === 200) {
          this.providerStats = res.data;
        } else {
          this.$message.error('获取提供商统计失败: ' + res.message);
        }
      } catch (err) {
        this.$message.error('请求失败: ' + err.message);
      }
    },
    async fetchDeviceTypeStats() {
      try {
        const res = await this.$axios.get('/api-30216/api/statistics/count');
        if (res.code === 200) {
          this.deviceTypeStats = res.data;
        } else {
          this.$message.error('获取设备类型统计失败: ' + res.message);
        }
      } catch (err) {
        this.$message.error('请求失败: ' + err.message);
      }
    },
    async fetchOrders() {
      try {
        const res = await this.$axios.get('/api-30216/api/statistics/orders');
        if (res.code === 200) {
          this.orders = res.data;
        } else {
          this.$message.error('获取订单失败: ' + res.message);
        }
      } catch (err) {
        this.$message.error('请求失败: ' + err.message);
      }
    },
    async fetchComputeTypes() {
      try {
        const res = await this.$axios.get('/api-30216/api/compute-types');
        if (res.code === 200) {
          this.computeTypes = res.data || [];
        } else {
          this.$message.error('获取计算类型失败: ' + res.message);
        }
      } catch (err) {
        this.$message.error('请求失败: ' + err.message);
      }
    },
    async fetchDevices() {
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        };
        if (this.selectedComputeTypes.length > 0) {
          params.ext_06 = this.selectedComputeTypes.join(',');
        }
        const res = await this.$axios.get('/api-30216/api/statistics/devices', { params });
        if (res.code === 200) {
          this.devices = res.data || [];
          this.pagination.total = res.total || this.devices.length;
        } else {
          this.$message.error('获取设备列表失败: ' + res.message);
        }
      } catch (err) {
        this.$message.error('请求失败: ' + err.message);
      }
    },
    handleTypeToggle(type) {
      const index = this.selectedComputeTypes.indexOf(type);
      if (index > -1) {
        this.selectedComputeTypes.splice(index, 1);
      } else {
        this.selectedComputeTypes.push(type);
      }
      this.pagination.page = 1;
      this.fetchDevices();
    },
    handlePageChange(page) {
      this.pagination.page = page;
      this.fetchDevices();
    },
    handlePageSizeChange(pageSize) {
      this.pagination.pageSize = pageSize;
      this.pagination.page = 1;
      this.fetchDevices();
    },
    getStatusText(state) {
      return state === 0 ? '空闲' : state === 1 ? '占用' : '维护中';
    },
    getStatusColor(state) {
      return state === 0 ? 'green' : state === 1 ? 'red' : 'orange';
    },
    getStatusTagType(state) {
      return state === 0 ? 'success' : state === 1 ? 'danger' : 'warning';
    }
  }
};
</script>

<style scoped>
.container {
  margin: 0 auto;
}
.stats-row {
  display: flex;
  justify-content: space-between;
}
.stat-item {
  flex: 1;
  text-align: center;
  padding: 10px;
  border: 1px solid #eee;
  border-radius: 8px;
  margin: 0 5px;
  background-color: #f9f9f9;
  height: 90px;
}
.value {
  font-size: 24px;
  font-weight: bold;
}
.simple-stats-row {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}
.simple-stat-item {
  display: flex;
  align-items: center;
  flex: 1;
}
.simple-label {
  font-weight: bold;
  margin-right: 20px;
  min-width: 60px;
}
.simple-value {
  font-size: 14px;
  margin-right: 15px;
}
.clearfix {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}
.clearfix > span,
.clearfix > .el-tag {
  line-height: 24px;
}
</style>